import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, CircularProgress, InputAdornment, SxProps, TextField, Typography } from "@mui/material";
import { getPassioFoodDisplayName, type PassioFoodDataInfo } from "@notemeal/passio-utils";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { MIN_SEARCH_TERM_LENGTH, usePassioFoodSearch } from "../../passio/usePassioFoodSearch";
import { useSnackbar } from "../Snackbar/SnackbarContext";

export type PassioSearchOption = {
  id: string;
  name: string;
  brandName?: string;
  source: PassioFoodDataInfo;
};

const getPassioSearchOption = (food: PassioFoodDataInfo & { refCode: string }): PassioSearchOption => ({
  id: food.refCode,
  name: getPassioFoodDisplayName(food),
  brandName: food.brandName,
  source: food,
});

interface PassioBrandedSearchBarProps {
  placeholder?: string;
  autoFocus?: boolean;
  sx?: SxProps;
  onSelect: (food: PassioFoodDataInfo) => void;
  getOptionSx?: (option: PassioSearchOption) => SxProps;
}

export const PassioBrandedSearchBar = ({
  placeholder = "Search branded foods",
  autoFocus,
  sx,
  onSelect,
  getOptionSx,
}: PassioBrandedSearchBarProps) => {
  const { setMessage } = useSnackbar();
  const [inputText, setInputText] = useState("");
  const handleSearchError = useCallback(() => setMessage("error", "Error searching foods, please try again."), [setMessage]);
  const { results, loading, searchConfigured } = usePassioFoodSearch({
    searchTerm: inputText,
    brandedOnly: true,
    onSearchError: handleSearchError,
  });

  const options = useMemo(
    () => results.filter((food): food is PassioFoodDataInfo & { refCode: string } => !!food.refCode).map(getPassioSearchOption),
    [results]
  );

  const trimmedInputLength = inputText.trim().length;
  const showLoading = loading && trimmedInputLength >= MIN_SEARCH_TERM_LENGTH;
  let noOptionsText: ReactNode = <>Start typing to search</>;
  if (searchConfigured === false && trimmedInputLength >= MIN_SEARCH_TERM_LENGTH) {
    noOptionsText = <>Branded food search is unavailable</>;
  } else if (showLoading) {
    noOptionsText = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} /> Loading...
      </Box>
    );
  } else if (trimmedInputLength >= MIN_SEARCH_TERM_LENGTH) {
    noOptionsText = <>No results</>;
  }

  return (
    <Autocomplete
      value={null}
      inputValue={inputText}
      autoComplete
      filterOptions={x => x}
      options={options}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={noOptionsText}
      onChange={(_, newValue) => {
        if (newValue) {
          setInputText("");
          onSelect(newValue.source);
        }
      }}
      onInputChange={(_, searchTerm) => {
        setInputText(searchTerm ?? "");
      }}
      onBlur={() => setInputText("")}
      renderInput={params => (
        <TextField
          {...params}
          sx={{ minWidth: 230, mt: 0, ...sx }}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            autoFocus,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: null,
          }}
        />
      )}
      renderOption={(props, option, { inputValue }) => {
        const label = option.name;
        const labelMatches = match(label, inputValue, { insideWords: true });
        const labelParts = parse(label, labelMatches);

        return (
          <li {...props}>
            <div>
              <Typography sx={getOptionSx?.(option)}>
                {labelParts.map(({ highlight, text }, index) => (
                  <span key={index} style={{ fontWeight: highlight ? 700 : 400 }}>
                    {text}
                  </span>
                ))}
              </Typography>
              {option.brandName && (
                <Typography sx={theme => ({ color: theme.palette.mediumEmphasisText })} variant="subtitle1">
                  {option.brandName}
                </Typography>
              )}
            </div>
          </li>
        );
      }}
      componentsProps={{ popupIndicator: { sx: { display: "none" } } }}
    />
  );
};
